import React, { useEffect, useMemo, useState } from "react";
import { lookupVendorMakes } from "services/vendorMakeService";

const getSelectedVendorType = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.category) && appliedFilters.category.length > 0
    ? appliedFilters.category[0]
    : "";

const getSelectedTaxonomyId = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.vendorCategory) &&
  appliedFilters.vendorCategory.length > 0
    ? appliedFilters.vendorCategory[0]
    : "";

const getSelectedMakeId = (appliedFilters = {}) =>
  Array.isArray(appliedFilters.vendorMake) && appliedFilters.vendorMake.length > 0
    ? appliedFilters.vendorMake[0]
    : "";

const normalizeSearch = (value = "") => value.toString().trim().toLowerCase();

const VendorCategoryQuickFilter = ({
  taxonomyAll = {},
  appliedFilters = {},
  applyFilterData,
  clearAllVendorCategoryFilters,
  vendorTypeCounts = {},
  disciplineCounts = {},
  categoryCounts = {},
  subCategoryCounts = {},
  makeCounts = {},
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [makeRetry, setMakeRetry] = useState(0);
  const [makeLookup, setMakeLookup] = useState({
    subCategoryId: "",
    status: "idle",
    options: [],
  });

  const vendorTypes = useMemo(() => Object.keys(taxonomyAll), [taxonomyAll]);
  const selectedVendorType = getSelectedVendorType(appliedFilters);
  const selectedTaxonomyId = getSelectedTaxonomyId(appliedFilters);
  const selectedMakeId = getSelectedMakeId(appliedFilters);
  const normalizedSearchText = normalizeSearch(searchText);

  const taxonomyIndex = useMemo(() => {
    const index = {
      disciplines: {},
      categories: {},
      subCategories: {},
    };

    Object.entries(taxonomyAll).forEach(([vendorType, data]) => {
      (data?.disciplines || []).forEach((discipline) => {
        index.disciplines[discipline._id] = {
          ...discipline,
          vendorType,
        };

        (discipline.categories || []).forEach((category) => {
          index.categories[category._id] = {
            ...category,
            vendorType,
            disciplineId: discipline._id,
            disciplineName: discipline.name,
          };

          (category.subCategories || []).forEach((subCategory) => {
            index.subCategories[subCategory._id] = {
              ...subCategory,
              vendorType,
              disciplineId: discipline._id,
              disciplineName: discipline.name,
              categoryId: category._id,
              categoryName: category.name,
            };
          });
        });
      });
    });

    return index;
  }, [taxonomyAll]);

  const selectedDisciplineId = useMemo(() => {
    if (taxonomyIndex.disciplines[selectedTaxonomyId]) {
      return selectedTaxonomyId;
    }

    if (taxonomyIndex.categories[selectedTaxonomyId]) {
      return taxonomyIndex.categories[selectedTaxonomyId].disciplineId;
    }

    if (taxonomyIndex.subCategories[selectedTaxonomyId]) {
      return taxonomyIndex.subCategories[selectedTaxonomyId].disciplineId;
    }

    return "";
  }, [selectedTaxonomyId, taxonomyIndex]);

  const selectedCategoryId = useMemo(() => {
    if (taxonomyIndex.categories[selectedTaxonomyId]) {
      return selectedTaxonomyId;
    }

    if (taxonomyIndex.subCategories[selectedTaxonomyId]) {
      return taxonomyIndex.subCategories[selectedTaxonomyId].categoryId;
    }

    return "";
  }, [selectedTaxonomyId, taxonomyIndex]);

  const selectedSubCategoryId = taxonomyIndex.subCategories[selectedTaxonomyId]
    ? selectedTaxonomyId
    : "";

  useEffect(() => {
    const subCategoryId = String(selectedSubCategoryId || "");

    if (selectedVendorType !== "Material" || !subCategoryId) {
      setMakeLookup({ subCategoryId: "", status: "idle", options: [] });
      return undefined;
    }

    const controller = new AbortController();
    setMakeLookup({ subCategoryId, status: "loading", options: [] });

    lookupVendorMakes([subCategoryId], [], controller.signal)
      .then((data) => {
        setMakeLookup({
          subCategoryId,
          status: "success",
          options: Array.isArray(data?.bySubCategory?.[subCategoryId])
            ? data.bySubCategory[subCategoryId]
            : [],
        });
      })
      .catch((error) => {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        setMakeLookup({ subCategoryId, status: "error", options: [] });
      });

    return () => controller.abort();
  }, [makeRetry, selectedSubCategoryId, selectedVendorType]);

  useEffect(() => {
    if (selectedVendorType) {
      setIsExpanded(true);
    }
  }, [selectedVendorType]);

  const visibleDisciplines = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    const disciplines = taxonomyAll[selectedVendorType].disciplines || [];

    if (!normalizedSearchText) {
      return disciplines;
    }

    return disciplines.filter((discipline) => {
      const disciplineMatches = normalizeSearch(discipline?.name).includes(
        normalizedSearchText,
      );
      const categoryMatches = (discipline?.categories || []).some(
        (category) =>
          normalizeSearch(category?.name).includes(normalizedSearchText) ||
          (category?.subCategories || []).some((subCategory) =>
            normalizeSearch(subCategory?.name).includes(normalizedSearchText),
          ),
      );

      return disciplineMatches || categoryMatches;
    });
  }, [normalizedSearchText, selectedVendorType, taxonomyAll]);

  const visibleCategories = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    if (normalizedSearchText) {
      return (taxonomyAll[selectedVendorType].disciplines || []).flatMap(
        (discipline) =>
          (discipline?.categories || []).filter(
            (category) =>
              normalizeSearch(category?.name).includes(normalizedSearchText) ||
              (category?.subCategories || []).some((subCategory) =>
                normalizeSearch(subCategory?.name).includes(normalizedSearchText),
              ),
          ),
      );
    }

    if (!selectedDisciplineId || !taxonomyIndex.disciplines[selectedDisciplineId]) {
      return [];
    }

    return taxonomyIndex.disciplines[selectedDisciplineId].categories || [];
  }, [
    normalizedSearchText,
    selectedDisciplineId,
    selectedVendorType,
    taxonomyAll,
    taxonomyIndex,
  ]);

  const visibleSubCategories = useMemo(() => {
    if (!selectedVendorType || !taxonomyAll[selectedVendorType]) {
      return [];
    }

    if (normalizedSearchText) {
      return (taxonomyAll[selectedVendorType].disciplines || []).flatMap(
        (discipline) =>
          (discipline?.categories || []).flatMap((category) =>
            (category?.subCategories || [])
              .filter((subCategory) =>
                normalizeSearch(subCategory?.name).includes(normalizedSearchText),
              )
              .map((subCategory) => ({
                ...subCategory,
                categoryId: category?._id,
                categoryName: category?.name,
                disciplineId: discipline?._id,
                disciplineName: discipline?.name,
              })),
          ),
      );
    }

    if (!selectedCategoryId || !taxonomyIndex.categories[selectedCategoryId]) {
      return [];
    }

    return taxonomyIndex.categories[selectedCategoryId].subCategories || [];
  }, [
    normalizedSearchText,
    selectedCategoryId,
    selectedVendorType,
    taxonomyAll,
    taxonomyIndex,
  ]);

  const applyQuickFilter = ({
    vendorType = "",
    taxonomyId = "",
    makeId = "",
  }) => {
    applyFilterData({
      ...appliedFilters,
      category: vendorType ? [vendorType] : [],
      vendorCategory: taxonomyId ? [taxonomyId] : [],
      vendorMake: makeId ? [makeId] : [],
    });
  };

  const handleVendorTypeClick = (vendorType) => {
    if (selectedVendorType === vendorType) {
      setIsExpanded(false);
      setSearchText("");
      clearAllVendorCategoryFilters();
      return;
    }

    setIsExpanded(true);
    applyQuickFilter({ vendorType, taxonomyId: "" });
  };

  const handleDisciplineClick = (disciplineId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === disciplineId) {
      applyQuickFilter({ vendorType: selectedVendorType, taxonomyId: "" });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: disciplineId,
    });
  };

  const handleCategoryClick = (categoryId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === categoryId) {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedDisciplineId,
      });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: categoryId,
    });
  };

  const handleSubCategoryClick = (subCategoryId) => {
    if (!selectedVendorType) return;

    if (selectedTaxonomyId === subCategoryId) {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedCategoryId,
      });
      return;
    }

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: subCategoryId,
    });
  };

  const handleMakeClick = (makeId) => {
    if (!selectedVendorType || !selectedSubCategoryId) return;

    applyQuickFilter({
      vendorType: selectedVendorType,
      taxonomyId: selectedSubCategoryId,
      makeId: selectedMakeId === makeId ? "" : makeId,
    });
  };

  const selectedFilterChips = useMemo(() => {
    const chips = [];

    if (selectedVendorType) {
      chips.push({
        id: selectedVendorType,
        label: selectedVendorType,
        type: "vendorType",
      });
    }

    if (selectedDisciplineId && taxonomyIndex.disciplines[selectedDisciplineId]) {
      chips.push({
        id: selectedDisciplineId,
        label: taxonomyIndex.disciplines[selectedDisciplineId].name,
        type: "discipline",
      });
    }

    if (selectedCategoryId && taxonomyIndex.categories[selectedCategoryId]) {
      chips.push({
        id: selectedCategoryId,
        label: taxonomyIndex.categories[selectedCategoryId].name,
        type: "category",
      });
    }

    if (selectedSubCategoryId && taxonomyIndex.subCategories[selectedSubCategoryId]) {
      chips.push({
        id: selectedSubCategoryId,
        label: taxonomyIndex.subCategories[selectedSubCategoryId].name,
        type: "subCategory",
      });
    }

    if (selectedMakeId) {
      const selectedMake = makeLookup.options.find(
        (make) => String(make?._id) === String(selectedMakeId),
      );

      chips.push({
        id: selectedMakeId,
        label: selectedMake?.name || "Make / Brand",
        type: "make",
      });
    }

    return chips;
  }, [
    makeLookup.options,
    selectedCategoryId,
    selectedDisciplineId,
    selectedMakeId,
    selectedSubCategoryId,
    selectedVendorType,
    taxonomyIndex,
  ]);

  const handleChipRemove = (chip) => {
    if (chip.type === "vendorType") {
      setIsExpanded(false);
      setSearchText("");
      clearAllVendorCategoryFilters();
      return;
    }

    if (chip.type === "discipline") {
      applyQuickFilter({ vendorType: selectedVendorType, taxonomyId: "" });
      return;
    }

    if (chip.type === "category") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedDisciplineId,
      });
      return;
    }

    if (chip.type === "subCategory") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedCategoryId,
      });
      return;
    }

    if (chip.type === "make") {
      applyQuickFilter({
        vendorType: selectedVendorType,
        taxonomyId: selectedSubCategoryId,
      });
    }
  };

  const showDetailPanels = Boolean(selectedVendorType) && isExpanded;
  const hasAppliedFilters = selectedFilterChips.length > 0;
  return (
    <div className="overflow-hidden rounded-xl border border-[#E8E8E0] bg-white shadow-sm">
      <div className="border-b border-[#ECECE3] bg-gradient-to-r from-[#FFF0E4] via-[#FFF8F1] to-[#F3F6FF] px-3 py-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1.5">
              {vendorTypes.map((vendorType) => (
                <button
                  key={vendorType}
                  onClick={() => handleVendorTypeClick(vendorType)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                    selectedVendorType === vendorType
                      ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                      : "border-[#DADAD0] bg-white text-[#555548] hover:border-[#E95420]"
                  }`}
                >
                  {vendorType}
                  <span className="ml-1 text-gray-500">
                    ({vendorTypeCounts[vendorType] || 0})
                  </span>
                </button>
              ))}
            </div>

            {hasAppliedFilters && (
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="shrink-0 text-[11px] text-gray-600">
                  Applied Filters:
                </span>

                {selectedFilterChips.map((chip) => (
                  <div
                    key={`${chip.type}-${chip.id}`}
                    className="flex items-center gap-1 rounded-full border border-[#F7B99D] bg-[#FFF4EE] px-2 py-[2px] text-[11px] text-[#B43A12]"
                  >
                    <span>{chip.label}</span>
                    <button
                      type="button"
                      onClick={() => handleChipRemove(chip)}
                      className="font-bold leading-none hover:text-red-500"
                    >
                      x
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  className="shrink-0 text-[11px] font-semibold text-[#E95420] hover:underline"
                  onClick={() => {
                    setIsExpanded(false);
                    setSearchText("");
                    clearAllVendorCategoryFilters();
                  }}
                >
                  Clear All
                </button>
              </div>
            )}
          </div>

           {selectedVendorType && isExpanded && (
              <input
                type="text"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search discipline, category, or sub category"
                className="min-w-[200px] max-w-[300px] flex-1 rounded-lg border border-[#DADAD0] bg-white px-3 py-2 text-xs text-gray-700 placeholder:text-gray-400 focus:border-[#E95420] focus:outline-none focus:ring-0"
              />
            )}
            {selectedVendorType && (
              <button
                type="button"
                onClick={() => setIsExpanded((previous) => !previous)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-semibold text-[#E95420] hover:bg-[#FFF4EE]"
              >
                {isExpanded ? "Minimize" : "Expand"}
              </button>
            )}
        </div>
      </div>

      {showDetailPanels && (
        <div className="p-2">
          <div
            className={`grid gap-2 ${selectedVendorType === "Material" ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
          >
          <div className="flex max-h-[190px] flex-col rounded-lg border border-[#E8E8E0]">
            <div className="border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  1
                </span>
                Discipline
              </span>
            </div>

            <div className="flex flex-wrap gap-2 overflow-auto p-2">
              {visibleDisciplines.length > 0 ? (
                visibleDisciplines.map((discipline) => (
                  <button
                    key={discipline._id}
                    onClick={() => handleDisciplineClick(discipline._id)}
                    className={`rounded-full border px-2 py-1 text-[11px] ${
                      selectedDisciplineId === discipline._id
                        ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                        : "border-[#DADAD0] bg-white text-gray-700"
                    }`}
                    title={
                      normalizedSearchText && discipline?.name
                        ? `Discipline: ${discipline.name}`
                        : undefined
                    }
                  >
                    {discipline.name}
                    <span className="ml-1 text-gray-500">
                      ({disciplineCounts[discipline._id] || 0})
                    </span>
                  </button>
                ))
              ) : (
                <div className="text-xs text-gray-500">
                  No disciplines available for this main category
                </div>
              )}
            </div>
          </div>

          <div className="flex max-h-[190px] flex-col rounded-lg border border-[#E8E8E0]">
            <div className="border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  2
                </span>
                Categories
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {selectedDisciplineId ? (
                <div className="flex flex-wrap gap-2">
                  {visibleCategories.length > 0 ? (
                    visibleCategories.map((category) => (
                      <button
                        key={category._id}
                        onClick={() => handleCategoryClick(category._id)}
                        className={`rounded-full border px-2 py-[2px] text-[11px] ${
                          selectedCategoryId === category._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-[#DADAD0] bg-white text-gray-700"
                        }`}
                        title={
                          normalizedSearchText
                            ? [
                                category?.disciplineName &&
                                  `Discipline: ${category.disciplineName}`,
                                category?.name && `Category: ${category.name}`,
                              ]
                                .filter(Boolean)
                                .join(" | ")
                            : undefined
                        }
                      >
                        {category.name}
                        <span className="ml-1 text-gray-500">
                          ({categoryCounts[category._id] || 0})
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No categories available for this discipline
                    </div>
                  )}
                </div>
              ) : normalizedSearchText ? (
                <div className="flex flex-wrap gap-2">
                  {visibleCategories.length > 0 ? (
                    visibleCategories.map((category) => (
                      <button
                        key={category._id}
                        onClick={() => handleCategoryClick(category._id)}
                        className={`rounded-full border px-2 py-[2px] text-[11px] ${
                          selectedCategoryId === category._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-[#DADAD0] bg-white text-gray-700"
                        }`}
                        title={
                          category?.disciplineName
                            ? `Discipline: ${category.disciplineName}`
                            : undefined
                        }
                      >
                        {category.name}
                        <span className="ml-1 text-gray-500">
                          ({categoryCounts[category._id] || 0})
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No categories match your search
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  Select a discipline to view categories
                </div>
              )}
            </div>
          </div>

          <div className="flex max-h-[190px] flex-col rounded-lg border border-[#E8E8E0]">
            <div className="border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                  3
                </span>
                Sub Categories
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {selectedCategoryId ? (
                <div className="flex flex-wrap gap-1.5">
                  {visibleSubCategories.length > 0 ? (
                    visibleSubCategories.map((subCategory) => (
                      <button
                        key={subCategory._id}
                        onClick={() => handleSubCategoryClick(subCategory._id)}
                        className={`rounded-full border px-2 py-[2px] text-[11px] ${
                          selectedSubCategoryId === subCategory._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-[#DADAD0] bg-white text-gray-700"
                        }`}
                        title={
                          normalizedSearchText
                            ? [
                                subCategory?.categoryName &&
                                  `Category: ${subCategory.categoryName}`,
                                subCategory?.name &&
                                  `Sub Category: ${subCategory.name}`,
                              ]
                                .filter(Boolean)
                                .join(" | ")
                            : undefined
                        }
                      >
                        {subCategory.name}
                        <span className="ml-1 text-gray-500">
                          ({subCategoryCounts[subCategory._id] || 0})
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No sub categories available for this category
                    </div>
                  )}
                </div>
              ) : normalizedSearchText ? (
                <div className="flex flex-wrap gap-1.5">
                  {visibleSubCategories.length > 0 ? (
                    visibleSubCategories.map((subCategory) => (
                      <button
                        key={subCategory._id}
                        onClick={() => handleSubCategoryClick(subCategory._id)}
                        className={`rounded-full border px-2 py-[2px] text-[11px] ${
                          selectedSubCategoryId === subCategory._id
                            ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                            : "border-[#DADAD0] bg-white text-gray-700"
                        }`}
                        title={
                          subCategory?.categoryName
                            ? `Category: ${subCategory.categoryName}`
                            : undefined
                        }
                      >
                        {subCategory.name}
                        <span className="ml-1 text-gray-500">
                          ({subCategoryCounts[subCategory._id] || 0})
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="text-xs text-gray-500">
                      No sub categories match your search
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-xs text-gray-500">
                  Select a category to view subcategories
                </div>
              )}
            </div>
          </div>

          {selectedVendorType === "Material" && (
            <div className="flex max-h-[190px] flex-col rounded-lg border border-[#E8E8E0]">
              <div className="border-b border-[#E8E8E0] bg-[#F8F8F2] px-3 py-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#303026]">
                  <span className="mr-2 rounded-full bg-[#E95420] px-1.5 text-[11px] text-white">
                    4
                  </span>
                  Makes / Brands
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-2">
                {!selectedSubCategoryId ? (
                  <div className="text-xs text-gray-500">
                    Select a subcategory to view makes / brands
                  </div>
                ) : makeLookup.status === "loading" ? (
                  <div className="text-xs text-gray-500">
                    Loading makes / brands...
                  </div>
                ) : makeLookup.status === "error" ? (
                  <div className="text-xs text-red-600">
                    Unable to load makes / brands.{" "}
                    <button
                      type="button"
                      onClick={() => setMakeRetry((value) => value + 1)}
                      className="font-semibold underline"
                    >
                      Retry
                    </button>
                  </div>
                ) : makeLookup.options.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {makeLookup.options.map((make) => {
                      const makeId = String(make?._id || "");

                      return (
                        <button
                          key={makeId}
                          type="button"
                          onClick={() => handleMakeClick(makeId)}
                          className={`rounded-full border px-2 py-[2px] text-[11px] ${
                            String(selectedMakeId) === makeId
                              ? "border-[#E95420] bg-[#FFF4EE] text-[#B43A12]"
                              : "border-[#DADAD0] bg-white text-gray-700"
                          }`}
                        >
                          {make.name}
                          <span className="ml-1 text-gray-500">
                            ({makeCounts[makeId] || 0})
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500">
                    No makes / brands available for this subcategory
                  </div>
                )}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

    </div>
  );
};

export default VendorCategoryQuickFilter;
